import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { PropsWithChildren } from "react";
import { useAppSession } from "./AppProviders";
import {
  APP_LOCALE_CHANGED_EVENT,
  resolveCurrentAppLocale,
  setGuestLocaleOverride,
  setUserLocaleOverride,
  type AppLocale,
} from "@/lib/i18n/locale";
import { fetchUserProfilePreferences, readLocalProfilePreferences } from "@/lib/profile/preferences";

type AppLocaleContextValue = {
  locale: AppLocale;
  isProfileLocaleLoading: boolean;
  setLocale: (locale: AppLocale) => void;
};

const AppLocaleContext = createContext<AppLocaleContextValue | null>(null);

function readInitialLocale() {
  if (typeof window === "undefined") {
    return "ko" as AppLocale;
  }

  return readLocalProfilePreferences(window.localStorage)?.locale ?? resolveCurrentAppLocale();
}

export function LocaleProvider({ children, initialLocale }: PropsWithChildren<{ initialLocale?: AppLocale }>) {
  const { user } = useAppSession();
  const [locale, setLocaleState] = useState<AppLocale>(() => initialLocale ?? readInitialLocale());
  const [isProfileLocaleLoading, setIsProfileLocaleLoading] = useState(false);

  useEffect(() => {
    if (!user?.id) {
      setLocaleState(readLocalProfilePreferences(typeof window === "undefined" ? undefined : window.localStorage)?.locale ?? resolveCurrentAppLocale());
      setIsProfileLocaleLoading(false);
      return;
    }

    let cancelled = false;
    setLocaleState(resolveCurrentAppLocale());
    setIsProfileLocaleLoading(true);
    void fetchUserProfilePreferences(user.id)
      .then((preferences) => {
        if (cancelled) {
          return;
        }
        setUserLocaleOverride(user.id, preferences.locale);
        setLocaleState(preferences.locale);
      })
      .catch(() => {
        if (!cancelled) {
          // Host/device locale remains a usable fallback when profile preferences are unavailable.
          setLocaleState(resolveCurrentAppLocale());
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsProfileLocaleLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    const syncFromStorage = () => setLocaleState(resolveCurrentAppLocale());
    const syncFromCustomEvent = (event: Event) => {
      const nextLocale = (event as CustomEvent<{ locale?: unknown }>).detail?.locale;
      if (nextLocale === "ko" || nextLocale === "en") {
        setLocaleState(nextLocale);
        return;
      }
      syncFromStorage();
    };

    window.addEventListener("storage", syncFromStorage);
    window.addEventListener(APP_LOCALE_CHANGED_EVENT, syncFromCustomEvent);
    return () => {
      window.removeEventListener("storage", syncFromStorage);
      window.removeEventListener(APP_LOCALE_CHANGED_EVENT, syncFromCustomEvent);
    };
  }, []);

  const setLocale = useCallback((nextLocale: AppLocale) => {
    setLocaleState(nextLocale);
    if (user?.id) {
      setUserLocaleOverride(user.id, nextLocale);
      return;
    }
    setGuestLocaleOverride(nextLocale);
  }, [user?.id]);

  const value = useMemo(() => ({ locale, isProfileLocaleLoading, setLocale }), [isProfileLocaleLoading, locale, setLocale]);

  return <AppLocaleContext.Provider value={value}>{children}</AppLocaleContext.Provider>;
}

export function useAppLocale() {
  const context = useContext(AppLocaleContext);
  if (!context) {
    return {
      locale: resolveCurrentAppLocale(),
      isProfileLocaleLoading: false,
      setLocale: (nextLocale: AppLocale) => {
        try {
          setGuestLocaleOverride(nextLocale);
        } catch {
          // Test doubles and blocked storage may omit the persistence helper.
        }
      },
    } satisfies AppLocaleContextValue;
  }
  return context;
}
